<script setup lang="ts">
  import { ref, computed, onMounted } from 'vue'
  import { useRouter } from 'vue-router'
  import SettingsPage from '@/components/app/settings/SettingsPage.vue'
  import {
    QuestionnairesService,
    type CustomerAdminQuestionnaire as Questionnaire
  } from '@/types/generated'
  import { TableCell, TableRow } from '@/components/ui/table'
  import { Button } from '@/components/ui/button'
  import TableOptionsButton from '@/components/admin/table/TableOptionsButton.vue'
  import { ROUTES } from '@/router/routes'
  import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
  } from '@/components/ui/dialog'
  import { genericErrorToast } from '@/lib/toasts'
  import { useToast } from '@/components/ui/toast'
  import PaginatedTableView, {
    type FetchDataOptions,
    type PaginatedResponse,
    type Filter
  } from '@/components/admin/PaginatedTable.vue'
  import { Badge } from '@/components/ui/badge'
  import { useLawFieldStore } from '@/store/lawFieldStore'
  import { useUserStore } from '@/shared/stores/userStore'
  import { useTranslation } from '@/composables/useTranslation'
  import { useLawTopicTranslation } from '@/composables/useLawTopicTranslation'
  import { useLawFieldTranslation } from '@/composables/useLawFieldTranslation'
  import {
    Pencil,
    CheckCircle2,
    X,
    EyeIcon,
    Link2,
    Copy,
    Trash,
    PlusIcon,
    QrCode,
    Loader
  } from 'lucide-vue-next'
  import QRCodeDialog from '@/components/admin/QRCodeDialog.vue'
  import AiEditIcon from '@/components/icons/AiEditIcon.vue'
  import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
  import { v4 as uuidv4 } from 'uuid'

  const { t } = useTranslation({
    en: {
      newQuestionnaire: 'New questionnaire',
      name: 'Name',
      lawFieldTopic: 'Law field/Topic',
      updatedBy: 'Updated by',
      lastUpdated: 'Last updated',
      type: 'Type',
      all: 'All',
      templates: 'Templates',
      questionnaires: 'Questionnaires',
      lawField: 'Law field',
      allLawFields: 'All Law Fields',
      lawTopic: 'Law topic',
      allLawTopics: 'All Law Topics',
      template: 'Template',
      preview: 'Preview',
      copyLink: 'Copy link',
      edit: 'Edit',
      duplicate: 'Duplicate',
      delete: 'Delete',
      confirmDeletion: 'Confirm Deletion',
      areYouSureDelete: 'Are you sure you want to delete the questionnaire',
      actionCannotBeUndone: 'This action cannot be undone.',
      cancel: 'Cancel',
      linkCopiedSuccessfully: 'Link copied successfully to clipboard!',
      qrCode: 'QR Code',
      qrCodeScanDescription: 'Scan this QR code to go directly to the questionnaire',
      copyLinkButton: 'Copy link',
      qrCodeSave: 'Save QR Code',
      linkCopied: 'Link copied',
      linkCopiedDescription: 'The link has been copied to the clipboard',
      error: 'Error',
      linkCopyError: 'The link could not be copied',
      renameWithAI: 'Rename with AI',
      aiTitleGenerated: 'AI Title Generated',
      aiTitleGeneratedDescription: 'AI has generated a new title for this questionnaire',
      aiTitleError: 'AI Title Error',
      aiTitleErrorDescription: 'Failed to generate AI title',
      jupus: 'Jupus',
      justNow: 'just now',
      oneMinuteAgo: '1 minute ago',
      minutesAgo: 'minutes ago',
      oneHourAgo: '1 hour ago',
      hoursAgo: 'hours ago',
      oneDayAgo: '1 day ago',
      daysAgo: 'days ago',
      oneWeekAgo: '1 week ago',
      weeksAgo: 'weeks ago',
      oneMonthAgo: '1 month ago',
      monthsAgo: 'months ago',
      oneYearAgo: '1 year ago',
      yearsAgo: 'years ago',
      ago: 'ago',
      sortBy: 'Sort by',
      newestFirst: 'Newest first',
      oldestFirst: 'Oldest first',
      recentlyModified: 'Recently modified',
      leastRecentlyModified: 'Least recently modified',
      nameAZ: 'Name A-Z',
      nameZA: 'Name Z-A'
    },
    de: {
      newQuestionnaire: 'Neuer Fragebogen',
      name: 'Name',
      lawFieldTopic: 'Rechtsgebiet/Thema',
      updatedBy: 'Aktualisiert von',
      lastUpdated: 'Zuletzt aktualisiert',
      type: 'Typ',
      all: 'Alle',
      templates: 'Vorlagen',
      questionnaires: 'Fragebögen',
      lawField: 'Rechtsgebiet',
      allLawFields: 'Alle Rechtsgebiete',
      lawTopic: 'Rechtsthema',
      allLawTopics: 'Alle Rechtsthemen',
      template: 'Vorlage',
      preview: 'Vorschau',
      copyLink: 'Link kopieren',
      edit: 'Bearbeiten',
      duplicate: 'Duplizieren',
      delete: 'Löschen',
      confirmDeletion: 'Löschung bestätigen',
      areYouSureDelete: 'Sind Sie sicher, dass Sie den Fragebogen löschen möchten',
      actionCannotBeUndone: 'Diese Aktion kann nicht rückgängig gemacht werden.',
      cancel: 'Abbrechen',
      linkCopiedSuccessfully: 'Link erfolgreich in die Zwischenablage kopiert!',
      qrCode: 'QR Code',
      qrCodeScanDescription: 'Scannen Sie diesen QR Code, um direkt zum Fragebogen zu gelangen',
      copyLinkButton: 'Link kopieren',
      qrCodeSave: 'QR Code speichern',
      linkCopied: 'Link kopiert',
      linkCopiedDescription: 'Der Link wurde in die Zwischenablage kopiert',
      error: 'Fehler',
      linkCopyError: 'Der Link konnte nicht kopiert werden',
      renameWithAI: 'Mit KI umbenennen',
      aiTitleGenerated: 'KI-Titel generiert',
      aiTitleGeneratedDescription: 'KI hat einen neuen Titel für diesen Fragebogen generiert',
      aiTitleError: 'KI-Titel Fehler',
      aiTitleErrorDescription: 'KI-Titel konnte nicht generiert werden',
      jupus: 'Jupus',
      justNow: 'gerade eben',
      oneMinuteAgo: 'vor 1 Minute',
      minutesAgo: 'Minuten',
      oneHourAgo: 'vor 1 Stunde',
      hoursAgo: 'Stunden',
      oneDayAgo: 'vor 1 Tag',
      daysAgo: 'Tagen',
      oneWeekAgo: 'vor 1 Woche',
      weeksAgo: 'Wochen',
      oneMonthAgo: 'vor 1 Monat',
      monthsAgo: 'Monaten',
      oneYearAgo: 'vor 1 Jahr',
      yearsAgo: 'Jahren',
      ago: 'vor',
      sortBy: 'Sortieren nach',
      newestFirst: 'Neueste zuerst',
      oldestFirst: 'Älteste zuerst',
      recentlyModified: 'Kürzlich geändert',
      leastRecentlyModified: 'Am wenigsten geändert',
      nameAZ: 'Name A-Z',
      nameZA: 'Name Z-A'
    }
  })

  const router = useRouter()
  const { toast } = useToast()

  const showDeleteModal = ref(false)

  // Copy to clipboard functionality
  const copyToClipboard = async (text: string) => {
    try {
      const fullUrl = `${window.location.origin}${text}`
      await navigator.clipboard.writeText(fullUrl)
      alert(t('linkCopiedSuccessfully'))
    } catch (err) {
      console.error('Async: Could not copy text: ', err)
      // Fallback for older browsers
      try {
        const textArea = document.createElement('textarea')
        textArea.value = `${window.location.origin}${text}`
        document.body.appendChild(textArea)
        textArea.focus()
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
        alert(t('linkCopiedSuccessfully'))
      } catch (fallbackErr) {
        console.error('Fallback: Could not copy text: ', fallbackErr)
      }
    }
  }
  const questionnaireToDelete = ref<Questionnaire | null>(null)
  const paginatedTableRef = ref<InstanceType<typeof PaginatedTableView> | null>(null)
  const editingQuestionnaire = ref<string | null>(null)
  const newTitle = ref('')
  const showQRCodeModal = ref(false)
  const qrCodeUrl = ref('')
  const qrCodeTitle = ref('')
  const isGeneratingTitle = ref(false)

  const { fetchLawFields, getLawFieldOptions, getLawTopicOptions } = useLawFieldStore()
  const { user, fetchUser } = useUserStore()
  const { translateLawTopic, lawTopicTranslations } = useLawTopicTranslation()
  const { translateLawField, lawFieldTranslations } = useLawFieldTranslation()

  const lawFields = computed(() => useLawFieldStore().lawFields)

  const allLawFieldOptions = computed(() => {
    if (!lawFields.value?.data) {
      return []
    }

    // Access lawFieldTranslations to ensure reactivity to locale changes
    const translations = lawFieldTranslations.value

    const options = Object.keys(lawFields.value.data)
      .map((field) => ({
        value: field,
        label: translations[field] || field
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
    return options
  })

  const allLawTopicOptions = computed(() => {
    if (!lawFields.value?.data) {
      return []
    }

    // Access lawTopicTranslations to ensure reactivity to locale changes
    const translations = lawTopicTranslations.value

    const allTopics = new Set<string>()
    Object.values(lawFields.value.data).forEach((topics) => {
      topics.forEach((topic) => allTopics.add(topic))
    })
    const options = Array.from(allTopics)
      .map((topic) => ({
        value: topic,
        label: translations[topic] || topic
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
    return options
  })

  const fetchQuestionnaires = async (
    options?: FetchDataOptions
  ): Promise<PaginatedResponse<unknown>> => {
    const filters = options?.filters || []
    const typeFilter = filters.find((filter) => filter.key === 'type')?.value
    const lawFieldFilter = filters.find((filter) => filter.key === 'law_field')?.value
    const lawTopicFilter = filters.find((filter) => filter.key === 'law_topic')?.value
    const orderingFilter = filters.find((filter) => filter.key === 'ordering')?.value

    // Build query parameters for server-side filtering
    const queryParams: any = {
      limit: options?.limit,
      offset: options?.offset,
      search: options?.search,
      ordering: orderingFilter || '-updated_at'
    }

    // Add type filter (is_jupus_template)
    if (typeFilter === 'templates') {
      queryParams.isJupusTemplate = true
    } else if (typeFilter === 'questionnaires') {
      queryParams.isJupusTemplate = false
    }

    // Add law field filter
    if (lawFieldFilter && lawFieldFilter !== 'all') {
      queryParams.lawField = lawFieldFilter
    }

    // Add law topic filter
    if (lawTopicFilter && lawTopicFilter !== 'all') {
      queryParams.lawTopic = lawTopicFilter
    }

    const response = await QuestionnairesService.questionnairesList(queryParams)

    return {
      results: response.results as unknown[],
      next: response.next ?? null,
      previous: response.previous ?? null,
      count: response.count ?? 0
    }
  }

  const filterableFields = computed<Filter[]>(() => {
    const fields = [
      {
        key: 'ordering',
        label: t('sortBy'),
        type: 'select' as const,
        value: '-updated_at',
        options: [
          { value: '-updated_at', label: t('recentlyModified') },
          { value: 'updated_at', label: t('leastRecentlyModified') },
          { value: '-created_at', label: t('newestFirst') },
          { value: 'created_at', label: t('oldestFirst') },
          { value: 'title', label: t('nameAZ') },
          { value: '-title', label: t('nameZA') }
        ]
      },
      {
        key: 'type',
        label: t('type'),
        type: 'select' as const,
        options: [
          { value: 'all', label: t('all') },
          { value: 'templates', label: t('templates') },
          { value: 'questionnaires', label: t('questionnaires') }
        ]
      },
      {
        key: 'law_field',
        label: t('lawField'),
        type: 'select' as const,
        options: [{ value: 'all', label: t('allLawFields') }, ...allLawFieldOptions.value]
      },
      {
        key: 'law_topic',
        label: t('lawTopic'),
        type: 'select' as const,
        options: [{ value: 'all', label: t('allLawTopics') }, ...allLawTopicOptions.value]
      }
    ]
    return fields
  })

  const getCreatorInitials = (name: string | undefined): string => {
    if (!name || name === t('jupus')) return 'J'

    const words = name.trim().split(' ')
    if (words.length === 1) {
      return words[0].charAt(0).toUpperCase()
    }

    return words
      .slice(0, 2)
      .map((word) => word.charAt(0).toUpperCase())
      .join('')
  }

  const createQuestionnaire = async () => {
    try {
      // Navigate to new questionnaire route without creating in database
      router.push(ROUTES.APP.SETTINGS.QUESTIONNAIRES.QUESTIONNAIRE_DETAIL.replace(':uuid', 'new'))
    } catch (error) {
      genericErrorToast()
    }
  }

  const openDeleteModal = (questionnaire: Questionnaire) => {
    questionnaireToDelete.value = questionnaire
    showDeleteModal.value = true
  }

  const closeDeleteModal = () => {
    showDeleteModal.value = false
    questionnaireToDelete.value = null
  }

  const confirmDelete = async () => {
    if (questionnaireToDelete.value) {
      try {
        await QuestionnairesService.questionnairesDestroy({
          uuid: questionnaireToDelete.value.uuid
        })
        closeDeleteModal()
        if (paginatedTableRef.value) {
          await paginatedTableRef.value.refreshData()
        }
      } catch (error) {
        genericErrorToast()
      }
    }
  }

  const startEditing = (questionnaire: Questionnaire) => {
    editingQuestionnaire.value = questionnaire.uuid
    newTitle.value = questionnaire.title
  }

  const cancelEditing = () => {
    editingQuestionnaire.value = null
    newTitle.value = ''
  }

  const saveNewTitle = async (questionnaire: Questionnaire) => {
    try {
      // Update the questionnaire object with the new title
      const updatedQuestionnaire = {
        ...questionnaire,
        title: newTitle.value
      }

      await QuestionnairesService.questionnairesUpdate({
        uuid: questionnaire.uuid,
        requestBody: updatedQuestionnaire
      })

      editingQuestionnaire.value = null
      newTitle.value = ''

      // Refresh the table
      if (paginatedTableRef.value) {
        paginatedTableRef.value.refreshData()
      }
    } catch (error) {
      console.error('Error updating questionnaire:', error)
      genericErrorToast()
    }
  }

  const openQRCodeModal = (questionnaire: Questionnaire) => {
    const externalUrl = ROUTES.CLIENT_PORTAL.EXTERNAL_QUESTIONNAIRE_REDIRECT.replace(
      ':org_questionnaire_creation_token',
      questionnaire.org_questionnaire_creation_token
    ).replace(
      ':questionnaire_questionnaire_creation_token',
      questionnaire.questionnaire_creation_token
    )

    qrCodeUrl.value = `${window.location.origin}${externalUrl}`
    qrCodeTitle.value = `${t('qrCode')} - ${questionnaire.title}`
    showQRCodeModal.value = true
  }

  const openPreview = (questionnaire: Questionnaire) => {
    const previewUrl = ROUTES.CLIENT_PORTAL.QUESTIONNAIRE_PREVIEW.replace(
      ':uuid',
      questionnaire.uuid
    )
    window.location.href = previewUrl
  }

  const generateAITitle = async (questionnaire: Questionnaire) => {
    if (isGeneratingTitle.value) return

    isGeneratingTitle.value = true
    try {
      const response = await QuestionnairesService.questionnairesGenerateTitleCreate({
        uuid: questionnaire.uuid
      })

      if (response && typeof response === 'object' && 'suggested_title' in response) {
        newTitle.value = (response as any).suggested_title
      }

      toast({
        title: t('aiTitleGenerated'),
        description: t('aiTitleGeneratedDescription'),
        variant: 'default'
      })
    } catch (error) {
      console.error('Error generating AI title:', error)
      toast({
        variant: 'destructive',
        title: t('aiTitleError'),
        description: error instanceof Error ? error.message : t('aiTitleErrorDescription')
      })
    } finally {
      isGeneratingTitle.value = false
    }
  }

  const formatRelativeTime = (dateString: string): string => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)

    if (diffInSeconds < 60) {
      return t('justNow')
    }

    const diffInMinutes = Math.floor(diffInSeconds / 60)
    if (diffInMinutes < 60) {
      return diffInMinutes === 1 ? t('oneMinuteAgo') : `${diffInMinutes} ${t('minutesAgo')}`
    }

    const diffInHours = Math.floor(diffInMinutes / 60)
    if (diffInHours < 24) {
      return diffInHours === 1 ? t('oneHourAgo') : `${diffInHours} ${t('hoursAgo')}`
    }

    const diffInDays = Math.floor(diffInHours / 24)
    if (diffInDays < 7) {
      return diffInDays === 1 ? t('oneDayAgo') : `${diffInDays} ${t('daysAgo')}`
    }

    const diffInWeeks = Math.floor(diffInDays / 7)
    const diffInMonths = Math.floor(diffInDays / 30)

    if (diffInMonths === 0) {
      return diffInWeeks === 1 ? t('oneWeekAgo') : `${diffInWeeks} ${t('weeksAgo')}`
    }

    if (diffInMonths < 12) {
      return diffInMonths === 1 ? t('oneMonthAgo') : `${diffInMonths} ${t('monthsAgo')}`
    }

    const diffInYears = Math.floor(diffInDays / 365)
    return diffInYears === 1 ? t('oneYearAgo') : `${diffInYears} ${t('yearsAgo')}`
  }

  onMounted(async () => {
    // Fetch required data
    const fetching = []

    if (!lawFields.value) {
      fetching.push(fetchLawFields())
    }

    if (!user) {
      fetching.push(fetchUser())
    }

    await Promise.all(fetching)
  })
</script>

<template>
  <SettingsPage>
    <template #actions>
      <Button
        variant="default"
        class="inline-flex items-center gap-x-2 rounded-lg border border-transparent bg-purple-600 px-3 py-2
          text-sm font-semibold text-white hover:bg-purple-700 focus:outline-none focus:ring-2
          focus:ring-blue-500 disabled:pointer-events-none disabled:opacity-50"
        @click="createQuestionnaire"
      >
        <PlusIcon class="h-4 w-4" />
        {{ t('newQuestionnaire') }}
      </Button>
    </template>

    <div class="space-y-4">
      <PaginatedTableView
        ref="paginatedTableRef"
        searchable-fields="title, description"
        :fetch-data="fetchQuestionnaires"
        :filterable-fields="filterableFields"
        class="questionnaire-table"
      >
        <template #table-head>
          <TableCell class="min-w-[250px] max-w-[350px]">{{ t('name') }}</TableCell>
          <TableCell class="w-[180px]">{{ t('lawFieldTopic') }}</TableCell>
          <TableCell class="w-[150px]">{{ t('updatedBy') }}</TableCell>
          <TableCell class="w-[130px]">{{ t('lastUpdated') }}</TableCell>
          <TableCell class="w-[60px]"></TableCell>
        </template>
        <template #table-row="{ items }">
          <TableRow
            v-for="questionnaire in items"
            :key="questionnaire.uuid"
            class="group hover:bg-gray-50"
          >
            <!-- Name column with inline editing -->
            <TableCell class="min-w-[250px] max-w-[350px] py-4">
              <div
                v-if="editingQuestionnaire === questionnaire.uuid"
                class="flex items-center gap-3"
              >
                <div class="relative flex-1">
                  <input
                    v-model="newTitle"
                    type="text"
                    class="w-full rounded-md border-gray-300 px-3 py-2 pr-12 text-sm focus:border-purple-500
                      focus:ring-purple-500"
                  />
                  <!-- AI Title Generation Button -->
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          class="absolute right-2 top-1/2 h-8 w-8 -translate-y-1/2 text-purple-600 hover:bg-purple-50
                            hover:text-purple-700"
                          :disabled="isGeneratingTitle"
                          @click="generateAITitle(questionnaire)"
                        >
                          <AiEditIcon
                            v-if="!isGeneratingTitle"
                            class="h-4 w-4"
                          />
                          <Loader
                            v-else
                            class="h-4 w-4 animate-spin"
                          />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent class="border-black bg-black text-white">
                        <p class="text-white">{{ t('renameWithAI') }}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  class="h-8 w-8 text-green-600 hover:text-green-700"
                  @click="saveNewTitle(questionnaire)"
                >
                  <CheckCircle2 class="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  class="h-8 w-8 text-red-600 hover:text-red-700"
                  @click="cancelEditing"
                >
                  <X class="h-4 w-4" />
                </Button>
              </div>
              <div
                v-else
                class="flex min-w-0 items-center justify-between"
              >
                <div class="flex min-w-0 flex-1 items-center gap-2">
                  <span
                    :class="
                      !questionnaire.is_jupus_template
                        ? `cursor-pointer truncate font-medium text-gray-900 transition-colors hover:text-purple-600
                          hover:underline`
                        : 'truncate font-medium text-gray-900'
                    "
                    :title="questionnaire.title"
                    @click="
                      !questionnaire.is_jupus_template
                        ? $router.push(
                            ROUTES.APP.SETTINGS.QUESTIONNAIRES.QUESTIONNAIRE_DETAIL.replace(
                              ':uuid',
                              questionnaire.uuid
                            )
                          )
                        : null
                    "
                  >
                    {{ questionnaire.title }}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    class="h-6 w-6 flex-shrink-0 rounded border border-gray-300 bg-white shadow-sm hover:bg-gray-50"
                    :disabled="questionnaire.is_jupus_template"
                    @click="startEditing(questionnaire)"
                  >
                    <Pencil class="h-4 w-4 text-gray-600" />
                  </Button>
                </div>
                <Badge
                  v-if="questionnaire.is_jupus_template"
                  class="ml-2 flex-shrink-0 bg-purple-100 text-purple-700"
                >
                  {{ t('template') }}
                </Badge>
              </div>
            </TableCell>

            <!-- Law field/Topic column -->
            <TableCell class="w-[180px] py-4">
              <div class="space-y-1 overflow-hidden">
                <div
                  class="truncate text-sm font-medium text-gray-900"
                  :title="translateLawField(questionnaire.law_field) || '-'"
                >
                  {{ translateLawField(questionnaire.law_field) || '-' }}
                </div>
                <div
                  class="truncate text-sm text-gray-600"
                  :title="translateLawTopic(questionnaire.law_topic) || '-'"
                >
                  {{ translateLawTopic(questionnaire.law_topic) || '-' }}
                </div>
              </div>
            </TableCell>

            <!-- Updated by column -->
            <TableCell class="w-[150px] py-4">
              <div
                v-if="questionnaire.is_jupus_template"
                class="flex min-w-0 items-center gap-2 overflow-hidden"
              >
                <div
                  class="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-purple-500 text-xs
                    font-medium text-white"
                >
                  J
                </div>
                <span class="truncate text-gray-900">{{ t('jupus') }}</span>
              </div>
              <div
                v-else
                class="flex min-w-0 items-center gap-2 overflow-hidden"
              >
                <div
                  class="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-500 text-xs font-medium
                    text-white"
                >
                  {{ getCreatorInitials(questionnaire.created_by_name) }}
                </div>
                <span class="truncate text-gray-900">
                  {{ questionnaire.created_by_name || t('jupus') }}
                </span>
              </div>
            </TableCell>

            <!-- Last updated column -->
            <TableCell class="w-[130px] py-4">
              <span class="whitespace-nowrap text-gray-600">
                {{ formatRelativeTime(questionnaire.updated_at) }}
              </span>
            </TableCell>

            <!-- Actions column -->
            <TableCell class="w-[60px] py-4">
              <TableOptionsButton
                :options="[
                  {
                    label: t('preview'),
                    icon: EyeIcon,
                    onClick: () => openPreview(questionnaire)
                  },
                  {
                    label: t('copyLink'),
                    icon: Link2,
                    onClick: () => {
                      const externalUrl =
                        ROUTES.CLIENT_PORTAL.EXTERNAL_QUESTIONNAIRE_REDIRECT.replace(
                          ':org_questionnaire_creation_token',
                          questionnaire.org_questionnaire_creation_token
                        ).replace(
                          ':questionnaire_questionnaire_creation_token',
                          questionnaire.questionnaire_creation_token
                        )
                      copyToClipboard(externalUrl)
                    }
                  },
                  {
                    label: t('qrCode'),
                    icon: QrCode,
                    onClick: () => openQRCodeModal(questionnaire)
                  },
                  // Only show edit option for non-templates
                  ...(!questionnaire.is_jupus_template
                    ? [
                        {
                          label: t('edit'),
                          to: ROUTES.APP.SETTINGS.QUESTIONNAIRES.QUESTIONNAIRE_DETAIL.replace(
                            ':uuid',
                            questionnaire.uuid
                          ),
                          icon: Pencil
                        }
                      ]
                    : []),
                  {
                    label: t('duplicate'),
                    onClick: async () => {
                      try {
                        const copy = await QuestionnairesService.questionnairesCopyCreate({
                          uuid: questionnaire.uuid
                        })
                        if (copy) {
                          router.push(
                            ROUTES.APP.SETTINGS.QUESTIONNAIRES.QUESTIONNAIRE_DETAIL.replace(
                              ':uuid',
                              copy.uuid
                            )
                          )
                        }
                      } catch (error) {
                        genericErrorToast()
                      }
                    },
                    icon: Copy
                  },
                  // Only show delete option for non-templates
                  ...(!questionnaire.is_jupus_template
                    ? [
                        {
                          label: t('delete'),
                          onClick: () => openDeleteModal(questionnaire),
                          icon: Trash
                        }
                      ]
                    : [])
                ]"
              />
            </TableCell>
          </TableRow>
        </template>
      </PaginatedTableView>
    </div>
  </SettingsPage>

  <!-- Delete Confirmation Modal -->
  <Dialog
    :open="showDeleteModal"
    @update:open="closeDeleteModal"
  >
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{{ t('confirmDeletion') }}</DialogTitle>
        <DialogDescription>
          {{ t('areYouSureDelete') }} "{{ questionnaireToDelete?.title }}"?
          {{ t('actionCannotBeUndone') }}
        </DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <Button
          variant="secondary"
          @click="closeDeleteModal"
        >
          {{ t('cancel') }}
        </Button>
        <Button
          variant="destructive"
          @click="confirmDelete"
        >
          {{ t('delete') }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>

  <!-- QR Code Modal -->
  <QRCodeDialog
    v-model="showQRCodeModal"
    :url="qrCodeUrl"
    :title="qrCodeTitle"
    :description="t('qrCodeScanDescription')"
    :copy-button-text="t('copyLinkButton')"
    :save-button-text="t('qrCodeSave')"
    :copied-title="t('linkCopied')"
    :copied-description="t('linkCopiedDescription')"
    :copy-error-title="t('error')"
    :copy-error-description="t('linkCopyError')"
  />
</template>

<style scoped>
  :deep(.questionnaire-table table) {
    table-layout: fixed;
    width: 100%;
  }
</style>
